import { useContext, useState } from "react";
import {
  Modal,
  Stack,
  Select,
  Button,
  Group,
  Text,
  ScrollArea,
  Box,
  Divider,
  Loader,
  Center,
  Checkbox,
} from "@mantine/core";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { useGetTags } from "../../api/tag";
import { useGetDistributePreview, useDistributeDocument } from "../../api/user";

export default function DistributeDocumentModal({ opened, onClose, template }) {
  const { user } = useContext(AuthContext);
  const currentEventId = getCurrentWorkspace(user).eventId;
  const [tagId, setTagId] = useState(null);
  const [shouldCreateAnnouncement, setShouldCreateAnnouncement] =
    useState(false);
  const [shouldEmailRecipients, setShouldEmailRecipients] = useState(false);

  const { data: tags } = useGetTags({
    eventId: currentEventId,
    type: "people",
  });

  const tagOptions = [
    { value: "all", label: "All ticket holders" },
    ...(tags ?? []).map((tag) => ({
      value: String(tag.id),
      label: `${tag.title} - (${tag.usageCount} users)`,
    })),
  ];

  const numericTagId = tagId && tagId !== "all" ? Number(tagId) : undefined;

  const { data: preview, isFetching: isPreviewLoading } =
    useGetDistributePreview({
      id: template?.id,
      tagId: numericTagId,
      enabled: opened,
    });

  const { mutate: distribute, isPending: isDistributing } =
    useDistributeDocument({ id: template?.id });

  const handleClose = () => {
    setTagId(null);
    setShouldCreateAnnouncement(false);
    setShouldEmailRecipients(false);
    onClose();
  };

  const handleDistribute = () => {
    distribute(
      {
        ...(numericTagId ? { tagId: numericTagId } : {}),
        shouldCreateAnnouncement,
        shouldEmailRecipients,
      },
      {
        onSuccess: handleClose,
      }
    );
  };

  const recipients = preview?.users ?? [];
  const total = preview?.total ?? 0;
  const canDistribute = !!tagId && total > 0 && !isDistributing;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Distribute ${template?.name ?? ""}`}
      size="lg"
    >
      <Stack>
        <Select
          label="Recipients"
          description="Choose who should receive this document"
          placeholder="Select a tag"
          data={tagOptions}
          value={tagId}
          onChange={setTagId}
          searchable
        />

        <Checkbox
          label="Create announcement"
          description="Notify recipients with an announcement that links to their documents"
          checked={shouldCreateAnnouncement}
          onChange={(event) =>
            setShouldCreateAnnouncement(event.currentTarget.checked)
          }
        />

        <Checkbox
          label="Send emails"
          description="Email each recipient a link that opens their document without signing in"
          checked={shouldEmailRecipients}
          onChange={(event) =>
            setShouldEmailRecipients(event.currentTarget.checked)
          }
        />

        <Divider label="Preview" labelPosition="center" />

        {!tagId && (
          <Text c="dimmed" size="sm" ta="center">
            Select a tag to see who will receive the document.
          </Text>
        )}

        {tagId && isPreviewLoading && (
          <Center>
            <Loader size="sm" />
          </Center>
        )}

        {tagId && !isPreviewLoading && (
          <Box>
            <Text size="sm" fw={500} mb="xs">
              {total} recipient{total === 1 ? "" : "s"}
            </Text>
            {total > 0 ? (
              <ScrollArea.Autosize mah={220}>
                <Stack gap={4}>
                  {recipients.map((u) => (
                    <Text key={u.userId} size="sm">
                      {u.firstName} {u.lastName}{" "}
                      <Text span c="dimmed" size="xs">
                        ({u.email})
                      </Text>
                    </Text>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            ) : (
              <Text c="dimmed" size="sm">
                No recipients match the selected tag.
              </Text>
            )}
            {preview?.withoutEmail > 0 && (
              <Text c="dimmed" size="sm" mt="xs">
                {preview.withoutEmail} ticket
                {preview.withoutEmail === 1 ? "" : "s"} without an email will be
                skipped
              </Text>
            )}
          </Box>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="light" color="gray" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleDistribute}
            loading={isDistributing}
            disabled={!canDistribute}
          >
            Distribute
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
